import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getApiErrorMessage } from "@/lib/api";
import {
  createCandidate,
  fetchCandidates,
  fetchProcess,
  type CandidateRow,
  type EnrollmentStatus,
  type ProcessDetail,
} from "@/lib/adminSelectionApi";

const STATUS_LABEL: Record<EnrollmentStatus, string> = {
  EM_PREENCHIMENTO: "Em preenchimento",
  RESPOSTAS_ENVIADAS: "Respostas enviadas",
  EM_AVALIACAO: "Em avaliação",
  CONCLUIDA: "Concluída",
};

export default function AdminSelectionCandidates() {
  const { processId = "" } = useParams();
  const navigate = useNavigate();
  const canAddCandidate = hasPermission(useApp((s) => s.account), "SELECAO.CANDIDATO.CADASTRAR");
  const [process, setProcess] = useState<ProcessDetail | null>(null);
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, list] = await Promise.all([fetchProcess(processId), fetchCandidates(processId)]);
      setProcess(p);
      setRows(list);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao carregar candidatos"));
    }
  }, [processId]);

  useEffect(() => { void load(); }, [load]);

  // Pausa o polling quando a aba interna está inativa (keep-alive); retoma ao reativar, sem duplicar timer.
  const tabActive = useTabActive();

  // Polling/refetch (no WebSocket) so "iniciou/enviou" updates while the panel is open.
  useEffect(() => {
    if (!tabActive) return;
    const id = setInterval(() => { void fetchCandidates(processId).then(setRows).catch(() => {}); }, 12000);
    return () => clearInterval(id);
  }, [processId, tabActive]);

  const add = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const created = await createCandidate(processId, name.trim());
      toast.success(`Candidato criado — código ${created.accessCode}`);
      setName("");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível criar o candidato"));
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text: string, ok: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(ok); }
    catch { toast.error("Não foi possível copiar"); }
  };

  const accessUrl = typeof window !== "undefined" ? `${window.location.origin}/selecao` : "/selecao";
  const candidateLink = (code: string) => `${accessUrl}?codigo=${code}`;
  const messageFor = (r: CandidateRow) =>
    `Olá! Seu código de acesso ao processo seletivo é: ${r.accessCode}\nAcesse: ${candidateLink(r.accessCode)}`;

  const share = async (r: CandidateRow) => {
    const url = candidateLink(r.accessCode);
    const text = `Processo Seletivo NatureXpress${process?.name ? ` — ${process.name}` : ""}. Seu código: ${r.accessCode}`;
    const nav = navigator as Navigator & { share?: (data: { title?: string; text?: string; url?: string }) => Promise<void> };
    if (typeof nav.share === "function") {
      try { await nav.share({ title: "Processo Seletivo", text, url }); }
      catch { /* usuário cancelou o compartilhamento */ }
    } else {
      await copy(`${text}\n${url}`, "Link copiado (compartilhamento não suportado neste navegador)");
    }
  };

  const isOpen = process?.status === "ABERTO";

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6">
      <button className="mb-3 text-sm text-muted-foreground underline" onClick={() => navigate("/admin/selecao")}>
        ← Processos
      </button>
      <h1 className="text-xl font-semibold">{process?.name ?? "Candidatos"}</h1>
      <p className="text-sm text-muted-foreground">Candidatos e códigos de acesso.</p>

      {!isOpen && (
        <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-3 text-sm">
          O processo precisa estar <strong>Aberto</strong> para cadastrar candidatos.
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs text-muted-foreground">Nome do candidato</label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void add(); }} placeholder="Nome completo" />
        </div>
        {canAddCandidate && (
          <Button onClick={() => void add()} disabled={creating || !name.trim() || !isOpen}>
            + Novo candidato
          </Button>
        )}
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left">
            <tr>
              <th className="px-3 py-2">Nome</th>
              <th className="px-3 py-2">Código</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Nota</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="px-3 py-4 text-muted-foreground" colSpan={5}>Nenhum candidato ainda.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.enrollmentId} className="border-t border-border">
                <td className="px-3 py-3 font-medium">{r.candidateName || "—"}</td>
                <td className="px-3 py-3">
                  <span className="font-mono tracking-widest">{r.accessCode}</span>
                  <div className="mt-1 max-w-[240px] break-all text-[11px] text-muted-foreground">{candidateLink(r.accessCode)}</div>
                </td>
                <td className="px-3 py-3"><Badge variant="secondary">{STATUS_LABEL[r.status]}</Badge></td>
                <td className="px-3 py-3 font-mono">
                  {r.finalScore == null ? "—" : `${r.finalScore.toFixed(2).replace(".", ",")}${r.partialScore ? " (parcial)" : ""}`}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button size="sm" onClick={() => void share(r)}>Compartilhar</Button>
                    <Button size="sm" variant="ghost" onClick={() => void copy(candidateLink(r.accessCode), "Link copiado")}>Copiar link</Button>
                    <Button size="sm" variant="ghost" onClick={() => void copy(r.accessCode, "Código copiado")}>Copiar código</Button>
                    <Button size="sm" variant="ghost" onClick={() => void copy(messageFor(r), "Mensagem copiada")}>Copiar mensagem</Button>
                    <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/selecao/candidatos/${r.enrollmentId}`)}>Abrir avaliação</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
